import React, { useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import {
  Container,
  Header,
  Segment,
  Form,
  Button,
  Message,
  Icon,
  Grid,
  TextArea,
} from 'semantic-ui-react';
import { Helmet } from '@plone/volto/helpers';
import { getApiPath, getAuthHeaders } from '../../api';

export default function DocumentCreate() {
  const history = useHistory();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [documentId, setDocumentId] = useState('');
  const [title, setTitle] = useState('');
  const [documentType, setDocumentType] = useState('TECH');
  const [domainCode, setDomainCode] = useState('SW');
  const [version, setVersion] = useState('0.1');
  const [classification, setClassification] = useState('internal');
  const [owner, setOwner] = useState('');
  const [author, setAuthor] = useState('');
  const [reviewer, setReviewer] = useState('');
  const [approver, setApprover] = useState('');
  const [location, setLocation] = useState('');
  const [notes, setNotes] = useState('');
  const [bodyMd, setBodyMd] = useState('');

  const handleCreate = async () => {
    if (!documentId || !title) {
      setError('Document ID and Title are required.');
      return;
    }
    setSaving(true);
    setError(null);

    try {
      const res = await fetch(`${getApiPath()}/@qms/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          document_id: documentId,
          title,
          document_type: documentType,
          domain_code: domainCode,
          version,
          classification,
          owner: owner || null,
          author: author || null,
          reviewer: reviewer || null,
          approver: approver || null,
          location: location || null,
          notes: notes || null,
          body_md: bodyMd || null,
        }),
      });

      if (res.status === 401) {
        window.location.href = `/login?return_url=${encodeURIComponent(window.location.pathname)}`;
        return;
      }
      if (res.status === 403) {
        setError(
          'Permission denied. You need the Manage Documents permission.',
        );
        setSaving(false);
        return;
      }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `${res.status} ${res.statusText}`);
      }

      history.push(
        `/qms/register/${encodeURIComponent(documentId)}`,
      );
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <Container style={{ paddingTop: '2em', paddingBottom: '3em' }}>
      <Helmet title="Create Document" />
      <Link to="/qms/register">&larr; Document Register</Link>

      <Header as="h1" style={{ marginTop: '0.5em' }}>
        <Icon name="plus" /> Create Controlled Document
      </Header>

      {error && (
        <Message negative>
          <Message.Header>Error</Message.Header>
          <p>{error}</p>
        </Message>
      )}

      <Form>
        <Segment>
          <Header as="h3">Metadata</Header>
          <Grid columns={2} stackable>
            <Grid.Column>
              <Form.Input
                label="Document ID"
                value={documentId}
                onChange={(e) => setDocumentId(e.target.value)}
                placeholder="e.g. SOP-QMS-001 or SRS-002"
                required
              />
              <Form.Input
                label="Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
              />
              <Form.Input
                label="Version"
                value={version}
                onChange={(e) => setVersion(e.target.value)}
              />
              <Form.Select
                label="Document Type"
                value={documentType}
                onChange={(e, { value }) => setDocumentType(value)}
                options={[
                  { key: 'POL', value: 'POL', text: 'Policy' },
                  { key: 'SOP', value: 'SOP', text: 'SOP' },
                  { key: 'WI', value: 'WI', text: 'Work Instruction' },
                  { key: 'FRM', value: 'FRM', text: 'Form / Template' },
                  { key: 'TECH', value: 'TECH', text: 'Technical Document' },
                  { key: 'REC', value: 'REC', text: 'Record' },
                  { key: 'EXT', value: 'EXT', text: 'External Document' },
                ]}
              />
              <Form.Select
                label="Domain"
                value={domainCode}
                onChange={(e, { value }) => setDomainCode(value)}
                options={[
                  { key: 'DC', value: 'DC', text: 'Document Control' },
                  { key: 'QMS', value: 'QMS', text: 'Quality Management' },
                  { key: 'RM', value: 'RM', text: 'Risk Management' },
                  { key: 'SW', value: 'SW', text: 'Software' },
                  { key: 'CM', value: 'CM', text: 'Change Management' },
                  { key: 'CS', value: 'CS', text: 'Cybersecurity' },
                  { key: 'UE', value: 'UE', text: 'Usability Engineering' },
                  { key: 'TR', value: 'TR', text: 'Translation' },
                  { key: 'REG', value: 'REG', text: 'Regulatory' },
                  { key: 'SUP', value: 'SUP', text: 'Supplier / Procurement' },
                  { key: 'HR', value: 'HR', text: 'Human Resources' },
                ]}
              />
            </Grid.Column>
            <Grid.Column>
              <Form.Select
                label="Classification"
                value={classification}
                onChange={(e, { value }) => setClassification(value)}
                options={[
                  { key: 'public', value: 'public', text: 'Public' },
                  { key: 'internal', value: 'internal', text: 'Internal' },
                  {
                    key: 'confidential',
                    value: 'confidential',
                    text: 'Confidential',
                  },
                ]}
              />
              <Form.Input
                label="Owner"
                value={owner}
                onChange={(e) => setOwner(e.target.value)}
              />
              <Form.Input
                label="Author"
                value={author}
                onChange={(e) => setAuthor(e.target.value)}
              />
              <Form.Input
                label="Reviewer"
                value={reviewer}
                onChange={(e) => setReviewer(e.target.value)}
              />
              <Form.Input
                label="Approver"
                value={approver}
                onChange={(e) => setApprover(e.target.value)}
              />
            </Grid.Column>
          </Grid>
          <Form.Input
            label="File Location"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="e.g. docs/qms/SOP-QMS-001_Quality_Policy.md"
          />
          <Form.Input
            label="Notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Segment>

        <Segment>
          <Header as="h3">Document Body (Markdown)</Header>
          <Form.Field
            control={TextArea}
            value={bodyMd}
            onChange={(e) => setBodyMd(e.target.value)}
            rows={15}
            style={{ fontFamily: 'monospace', fontSize: '0.9em' }}
            placeholder="Write document content in Markdown..."
          />
        </Segment>

        <Button primary loading={saving} onClick={handleCreate}>
          <Icon name="plus" /> Create Document
        </Button>
        <Button as={Link} to="/qms/register">
          Cancel
        </Button>
      </Form>
    </Container>
  );
}
